import { render } from 'preact-render-to-string';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { summaryRows } from 'shared/checkin-feedback';
import { BasicEmailWrapper, Heading, Line, PrimaryAction, SupportFooter } from './shared';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * One check-in question paired with the student's answer. Both are plain text:
 * the check-in form is a textarea, not the rich-text editor, so nothing here is
 * markup and nothing here needs sanitizing. Rendering the answer as escaped JSX
 * text is what keeps a student's literal `<b>` a literal `<b>`.
 */
export type CheckinAnswerItem = {
  prompt: string;
  answer: string;
};

type Props = {
  studentName: string;
  courseTitle: string;
  /** The check-in's calendar date as `YYYY-MM-DD` */
  checkinDate: string;
  answers: CheckinAnswerItem[];
  /** The course's check-ins tab, for the coach to see the run of days */
  courseUrl: string;
  /** The student edited this check-in after the first notification went out. */
  isUpdate?: boolean;
};

/**
 * Answers are already capped at 4000 characters each and 5 per check-in by the
 * data layer, so the worst case here is ~20KB — well short of the ~102KB where
 * Gmail clips a message. This cap is the belt to that pair of braces: it bounds
 * the email even if the submit-side caps are ever relaxed.
 */
const ANSWER_CHARACTER_LIMIT = 4000;

/**
 * `checkinDate` is a calendar date with no time and no zone. Parsing it locally
 * gives local midnight of that same day, and only the day, month and year are
 * printed, so no conversion can move it.
 */
export function formatCheckinDate(checkinDate: string) {
  const parsed = dayjs(checkinDate);
  return parsed.isValid() ? parsed.format('dddd, MMM D, YYYY') : checkinDate;
}

/**
 * Coach notification for one student's daily check-in. The answers are included
 * in full so a coach can read and reply from the inbox; helpdesks (FreeScout)
 * thread these onto one conversation per student via the Message-Id headers the
 * cron sets, and treat the Reply-To student as the customer.
 */
export function CheckinSubmitted({
  studentName,
  courseTitle,
  checkinDate,
  answers,
  courseUrl,
  isUpdate,
}: Props) {
  return render(
    <BasicEmailWrapper footer={<SupportFooter />}>
      <Heading>
        {studentName} · {isUpdate ? 'updated ' : ''}
        {standupDayLabel(checkinDate)}
      </Heading>
      <Line style="color:#636363;">in {courseTitle}</Line>
      {isUpdate && (
        <Line>
          <strong>These answers replace the ones sent earlier for this day.</strong>
        </Line>
      )}

      {answers.map((answer, index) => (
        <AnswerBlock key={index} prompt={answer.prompt} answer={answer.answer} />
      ))}

      <PrimaryAction href={courseUrl}>React or reply</PrimaryAction>
      <Line style="color:#636363;font-size:14px;">
        Reply to this email to reach {firstName(studentName)} directly.
      </Line>
    </BasicEmailWrapper>,
  );
}

function firstName(name: string) {
  return name.trim().split(/\s+/)[0] || name;
}

/** "Wednesday standup, Aug 19, 2026". */
export function standupDayLabel(checkinDate: string) {
  const parsed = dayjs(checkinDate);
  return parsed.isValid()
    ? `${parsed.format('dddd')} standup, ${parsed.format('MMM D, YYYY')}`
    : checkinDate;
}

type SummaryProps = {
  studentName: string;
  courseTitle: string;
  /** "Weekly summary, week 1" or "Sprint summary". */
  title: string;
  submittedAt: Date;
  timezone: string;
  body: string;
  /** The coach's Standups tab, to react or reply in the course. */
  courseUrl: string;
  /** Set for an update: when the student last changed the summary. */
  updatedAt?: Date;
};

/**
 * Coach notification for a weekly summary. Same thread as the student's
 * standups (the cron sets the threading headers), laid out as label and
 * answer rows the way the weekly review writes them.
 */
export function CheckinSummarySubmitted(props: SummaryProps) {
  const rows = summaryRows(props.body);
  return render(
    <BasicEmailWrapper footer={<SupportFooter />}>
      <Heading>
        {props.studentName} · {props.title} ·{' '}
        {props.updatedAt
          ? `updated ${dayjs(props.updatedAt).tz(props.timezone).format('dddd, MMM D')}`
          : `sent ${dayjs(props.submittedAt).tz(props.timezone).format('dddd, MMM D')}`}
      </Heading>
      <Line style="color:#636363;">in {props.courseTitle}</Line>
      {props.updatedAt && (
        <Line>
          <strong>This replaces the summary sent earlier for this week.</strong>
        </Line>
      )}
      <table style="width:100%;border-collapse:collapse;margin:1rem 0 1.5rem;color:#3f3f46;">
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              style={row.isNudge ? 'background-color:#fffbeb;' : 'border-top:1px solid #e2e8f0;'}
            >
              <th
                scope="row"
                style="text-align:left;vertical-align:top;font-weight:600;padding:8px 12px 8px 0;width:35%;"
              >
                {row.label}
              </th>
              <td style="vertical-align:top;padding:8px 0;white-space:pre-wrap;overflow-wrap:break-word;">
                {row.value.length > ANSWER_CHARACTER_LIMIT
                  ? `${row.value.slice(0, ANSWER_CHARACTER_LIMIT)}…`
                  : row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PrimaryAction href={props.courseUrl}>React or reply</PrimaryAction>
      <Line style="color:#636363;font-size:14px;">
        Reply to this email to reach {firstName(props.studentName)} directly.
      </Line>
    </BasicEmailWrapper>,
  );
}

function AnswerBlock({ prompt, answer }: CheckinAnswerItem) {
  const text =
    answer.length > ANSWER_CHARACTER_LIMIT ? `${answer.slice(0, ANSWER_CHARACTER_LIMIT)}…` : answer;
  return (
    <div style="margin-top:1.5rem;margin-bottom:1.5rem;">
      {!!prompt && <p style="margin-bottom:0.5rem;font-weight:600;color:#3f3f46;">{prompt}</p>}
      <div style="padding:16px;background-color:#f8fafc;border-left:4px solid #5850ec;color:#3f3f46;white-space:pre-wrap;overflow-wrap:break-word;">
        {text}
      </div>
    </div>
  );
}
